<template>
  <section class="customizer" aria-labelledby="customizer-heading">
    <header class="head">
      <h2 id="customizer-heading" class="subtitle">Personaliza tu {{ productLabel }}</h2>
      <p class="text">
        Sube tu foto, encuádrala como quieras y elige el formato. Te mostramos
        cómo va a quedar antes de enviar el pedido.
      </p>
    </header>

    <!-- El mismo patrón sirve para otros personalizables (tarea #22). -->
    <div class="type-switch" role="group" aria-label="Tipo de producto">
      <button
        v-for="type in productTypes"
        :key="type.id"
        type="button"
        class="type-btn"
        :class="{ 'is-selected': productType === type.id }"
        :aria-pressed="productType === type.id"
        @click="selectType(type.id)"
      >
        {{ type.label }}
      </button>
    </div>

    <div class="layout" :class="{ 'is-studio': useStudio }">
      <div class="left">
        <FileUploader
          :accepted-extensions="['png', 'jpg', 'jpeg', 'webp']"
          :accepted-mime-types="['image/png', 'image/jpeg', 'image/webp']"
          :max-size-mb="10"
          aria-label="Subir tu fotografía"
          helper-text="Arrastra tu foto aquí o haz clic para elegirla"
          @file-selected="onFileSelected"
          @file-cleared="onFileCleared"
          @file-error="onFileCleared"
        />

        <p v-if="imageError" class="err-msg" role="alert">{{ imageError }}</p>

        <!-- Estudio V2: dos paneles, controles de imagen y troquel real. Tras
             VUE_APP_FEATURE_CUSTOMIZER_V2, para poder volver a la vista previa
             anterior sin desplegar código (plan 26_09_2026, §8). -->
        <PuzzleStudio
          v-if="useStudio"
          ref="studio"
          :image="image"
          :size-id="sizeId"
          :pieces="pieces"
          @change="onStudioChange"
        />

        <div v-else-if="imageLoaded" class="preview-wrap">
          <canvas
            ref="canvas"
            class="preview-canvas"
            :width="canvasWidth"
            :height="canvasHeight"
            :aria-label="`Vista previa de tu ${productLabel}`"
            @pointerdown="startDrag"
            @pointermove="onDrag"
            @pointerup="endDrag"
            @pointercancel="endDrag"
            @pointerleave="endDrag"
          ></canvas>

          <label class="zoom">
            <span>Acercar</span>
            <input
              v-model.number="zoom"
              type="range"
              min="1"
              max="3"
              step="0.01"
              @input="draw"
            />
          </label>

          <p class="hint">Arrastra la imagen sobre la vista previa para encuadrarla.</p>
        </div>
      </div>

      <div class="right">
        <form class="custom-form" @submit.prevent="onSubmit">
          <label class="block">
            <span>Tamaño</span>
            <select v-model="sizeId" required @change="onSizeChange">
              <option v-for="size in sizes" :key="size.id" :value="size.id">
                {{ size.label }}
              </option>
            </select>
          </label>

          <label v-if="productType === 'rompecabezas'" class="block">
            <span>Número de piezas</span>
            <select v-model.number="pieces" required @change="draw">
              <option v-for="option in pieceOptions" :key="option" :value="option">
                {{ option }} piezas
              </option>
            </select>
          </label>

          <label class="block">
            <span>Estuche</span>
            <select v-model="caseOption">
              <option v-for="option in caseOptions" :key="option.id" :value="option.id">
                {{ option.label }}
              </option>
            </select>
          </label>

          <!-- Personalización libre: nombre, fecha o dedicatoria (R6). -->
          <label class="block">
            <span>Texto a personalizar (opcional)</span>
            <input
              v-model.trim="personalizationText"
              type="text"
              :maxlength="MAX_PERSONALIZATION"
              placeholder="Un nombre, una fecha, una dedicatoria…"
            />
            <small class="counter">
              {{ personalizationText.length }} / {{ MAX_PERSONALIZATION }} caracteres
            </small>
          </label>

          <h3 class="form-title">Tus datos</h3>

          <label class="block">
            <span>Nombre completo</span>
            <input v-model.trim="contactName" type="text" required />
          </label>

          <label class="block">
            <span>Correo electrónico</span>
            <input v-model.trim="contactEmail" type="email" required />
          </label>

          <label class="block">
            <span>WhatsApp (opcional)</span>
            <input v-model.trim="contactPhone" type="tel" placeholder="3001234567" />
          </label>

          <label class="block">
            <span>Notas (opcional)</span>
            <textarea v-model.trim="notes" rows="3" maxlength="300"></textarea>
          </label>

          <button type="submit" class="button btn--block submit-btn" :disabled="!isValid || isBusy">
            {{ submitLabel }}
          </button>

          <p class="legal-note">
            Al enviar tu pedido aceptas nuestra
            <router-link to="/privacidad">política de privacidad</router-link>.
            Usamos tu imagen únicamente para fabricar esta pieza.
          </p>

          <!-- Estados del procesamiento en servidor (tarea #21). -->
          <p v-if="status === 'building'" class="state-msg" role="status">
            {{ progressLabel || 'Preparando los archivos de producción…' }}
          </p>
          <p v-if="status === 'sending'" class="state-msg" role="status">Enviando tu imagen…</p>
          <p v-if="status === 'processing'" class="state-msg" role="status">
            Procesando la imagen. En cuanto esté lista te confirmamos por correo.
          </p>
          <p v-if="status === 'done'" class="ok-msg" role="status">{{ successMessage }}</p>
          <p v-if="status === 'error'" class="err-msg" role="alert">{{ errorMessage }}</p>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import FileUploader from './FileUploader.vue';
import { submitCustomOrder } from '@/services/print3dService';

// El estudio V2 (controles de imagen + troquel + archivos de producción) vive
// tras su propio flag: apagarlo devuelve la vista previa anterior sin desplegar.
const STUDIO_ENABLED = process.env.VUE_APP_FEATURE_CUSTOMIZER_V2 === 'true';

const MIN_WIDTH = 800;
const MIN_HEIGHT = 600;
const CANVAS_WIDTH = 600;
const MAX_PERSONALIZATION = 40;

const PRODUCTS = {
  rompecabezas: {
    label: 'rompecabezas',
    aspect: 297 / 210,
    // Oferta inicial acotada a A5 y A4 (plan 25_08_2026, R2). Ambos comparten
    // la proporción de la serie A (1:raíz de 2), así que el marco no cambia.
    sizes: [
      { id: 'puzzle-a5', label: 'A5 — 14.8 × 21 cm', pieces: [24, 48, 120] },
      { id: 'puzzle-a4', label: 'A4 — 21 × 29.7 cm', pieces: [48, 120, 300] },
    ],
    pieceOptions: [48, 120, 300],
    caseOptions: [
      { id: 'none', label: 'Sin estuche' },
      { id: 'carton', label: 'Estuche de cartón' },
      { id: 'print3d', label: 'Estuche impreso en 3D' },
    ],
  },
  estuche: {
    label: 'estuche',
    aspect: 1,
    sizes: [
      { id: 'case-cards', label: 'Estuche para tarjetas' },
      { id: 'case-glasses', label: 'Estuche para gafas' },
      { id: 'case-custom', label: 'Medida a convenir' },
    ],
    pieceOptions: [],
    caseOptions: [
      { id: 'matte', label: 'Acabado mate' },
      { id: 'glossy', label: 'Acabado brillante' },
    ],
  },
};

export default {
  name: 'PuzzleCustomizer',
  components: {
    FileUploader,
    // Diferido a propósito: el estudio arrastra el núcleo geométrico y
    // `clipper-lib`, y `Print3DPage` se importa de forma estática en el router.
    // Sin esto, todo eso viajaría en el bundle inicial de la web pública, que lo
    // descarga cualquier visitante aunque nunca abra el personalizador.
    PuzzleStudio: defineAsyncComponent(
      () => import(/* webpackChunkName: "print3d-customizer" */ './PuzzleStudio.vue')
    ),
  },
  data() {
    const initial = PRODUCTS.rompecabezas;
    return {
      productTypes: [
        { id: 'rompecabezas', label: 'Rompecabezas' },
        { id: 'estuche', label: 'Estuche' },
      ],
      productType: 'rompecabezas',
      sizeId: initial.sizes[0].id,
      pieces: initial.sizes[0].pieces[0],
      caseOption: initial.caseOptions[0].id,

      file: null,
      image: null,
      imageLoaded: false,
      imageError: '',

      zoom: 1,
      offsetX: 0,
      offsetY: 0,
      dragging: false,
      lastPointer: { x: 0, y: 0 },

      MAX_PERSONALIZATION,
      personalizationText: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      notes: '',

      status: 'idle', // idle | building | sending | processing | done | error
      progressLabel: '',
      successMessage: '',
      errorMessage: '',

      // Estado que publica el estudio V2 (umbral, simplificación, semilla,
      // orientación, rejilla y encuadre). Nulo mientras no haya foto.
      studio: null,
    };
  },
  computed: {
    product() {
      return PRODUCTS[this.productType];
    },
    productLabel() {
      return this.product.label;
    },
    sizes() {
      return this.product.sizes;
    },
    pieceOptions() {
      const size = this.sizes.find((s) => s.id === this.sizeId);
      return size?.pieces || this.product.pieceOptions;
    },
    caseOptions() {
      return this.product.caseOptions;
    },
    canvasWidth() {
      return CANVAS_WIDTH;
    },
    canvasHeight() {
      return Math.round(CANVAS_WIDTH / this.product.aspect);
    },
    // El estudio sólo aplica al rompecabezas: el estuche no tiene troquel.
    useStudio() {
      return STUDIO_ENABLED && this.imageLoaded && this.productType === 'rompecabezas';
    },
    isBusy() {
      return ['building', 'sending', 'processing'].includes(this.status);
    },
    isValid() {
      return (
        this.imageLoaded &&
        this.contactName.length > 0 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.contactEmail)
      );
    },
    submitLabel() {
      if (this.status === 'building') return 'Preparando archivos…';
      if (this.status === 'sending') return 'Enviando…';
      if (this.status === 'processing') return 'Procesando…';
      return 'Enviar mi pedido';
    },
  },
  beforeUnmount() {
    this.releaseImage();
  },
  methods: {
    selectType(id) {
      this.productType = id;
      const product = PRODUCTS[id];
      this.sizeId = product.sizes[0].id;
      this.pieces = product.sizes[0].pieces?.[0] ?? product.pieceOptions[0] ?? null;
      this.caseOption = product.caseOptions[0].id;
      this.$nextTick(this.draw);
    },
    onSizeChange() {
      // A5 y A4 no ofrecen los mismos cortes: reencaja la selección.
      if (this.pieceOptions.length && !this.pieceOptions.includes(this.pieces)) {
        this.pieces = this.pieceOptions[0];
      }
      this.$nextTick(this.draw);
    },

    // ---------- Imagen ----------
    onFileSelected(file) {
      this.releaseImage();
      this.imageError = '';
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        // Validación en cliente: una foto pequeña se ve pixelada al imprimirla.
        if (img.naturalWidth < MIN_WIDTH || img.naturalHeight < MIN_HEIGHT) {
          this.imageError = `La imagen es demasiado pequeña (${img.naturalWidth}×${img.naturalHeight} px). Necesitamos al menos ${MIN_WIDTH}×${MIN_HEIGHT} px para que se imprima bien.`;
          URL.revokeObjectURL(url);
          return;
        }
        this.image = img;
        this.file = file;
        this.imageLoaded = true;
        this.zoom = 1;
        this.offsetX = 0;
        this.offsetY = 0;
        this.$nextTick(this.draw);
      };
      img.onerror = () => {
        this.imageError = 'No pudimos leer la imagen. Prueba con otro archivo.';
        URL.revokeObjectURL(url);
      };
      img.src = url;
    },
    onFileCleared() {
      this.releaseImage();
    },
    releaseImage() {
      if (this.image?.src?.startsWith('blob:')) URL.revokeObjectURL(this.image.src);
      this.image = null;
      this.file = null;
      this.imageLoaded = false;
    },

    // ---------- Encuadre ----------
    startDrag(event) {
      if (!this.imageLoaded) return;
      this.dragging = true;
      this.lastPointer = { x: event.clientX, y: event.clientY };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    onDrag(event) {
      if (!this.dragging) return;
      this.offsetX += event.clientX - this.lastPointer.x;
      this.offsetY += event.clientY - this.lastPointer.y;
      this.lastPointer = { x: event.clientX, y: event.clientY };
      this.draw();
    },
    endDrag(event) {
      if (!this.dragging) return;
      this.dragging = false;
      event.currentTarget?.releasePointerCapture?.(event.pointerId);
    },

    // ---------- Render ----------
    draw() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.image) return;
      const ctx = canvas.getContext('2d');
      const { width, height } = canvas;

      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#F8FAFC';
      ctx.fillRect(0, 0, width, height);

      // Escala "cover" + zoom del usuario.
      const scale =
        Math.max(width / this.image.naturalWidth, height / this.image.naturalHeight) * this.zoom;
      const drawWidth = this.image.naturalWidth * scale;
      const drawHeight = this.image.naturalHeight * scale;

      // Limita el desplazamiento para que no queden bordes vacíos.
      const maxOffsetX = Math.max(0, (drawWidth - width) / 2);
      const maxOffsetY = Math.max(0, (drawHeight - height) / 2);
      this.offsetX = Math.min(Math.max(this.offsetX, -maxOffsetX), maxOffsetX);
      this.offsetY = Math.min(Math.max(this.offsetY, -maxOffsetY), maxOffsetY);

      const x = (width - drawWidth) / 2 + this.offsetX;
      const y = (height - drawHeight) / 2 + this.offsetY;
      ctx.drawImage(this.image, x, y, drawWidth, drawHeight);

      if (this.productType === 'rompecabezas') this.drawPuzzleGrid(ctx, width, height);
    },
    drawPuzzleGrid(ctx, width, height) {
      // Retícula orientativa: aproxima el número de piezas elegido.
      const ratio = width / height;
      const columns = Math.max(2, Math.round(Math.sqrt(this.pieces * ratio)));
      const rows = Math.max(2, Math.round(this.pieces / columns));

      ctx.save();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.65)';
      ctx.lineWidth = 1;
      for (let c = 1; c < columns; c += 1) {
        const x = (width / columns) * c;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let r = 1; r < rows; r += 1) {
        const y = (height / rows) * r;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      ctx.restore();
    },
    canvasToBlob() {
      return new Promise((resolve) => {
        const canvas = this.$refs.canvas;
        if (!canvas || !canvas.toBlob) {
          resolve(null);
          return;
        }
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85);
      });
    },

    onStudioChange(state) {
      this.studio = state;
    },

    /**
     * Genera los archivos de producción del estudio. Se hace ANTES de enviar y
     * en un paso propio porque en un móvil tarda segundos: si fallara, el
     * usuario debe saber que no se envió nada, no quedarse con un pedido a medias.
     */
    async buildStudioArtifacts() {
      const artifacts = await this.$refs.studio.buildArtifacts((label) => {
        this.progressLabel = label;
      });
      return {
        die: { blob: artifacts.die.blob, filename: artifacts.die.filename, meta: artifacts.die.meta },
        svg: { blob: artifacts.image.blob, filename: artifacts.image.filename, meta: artifacts.image.meta },
      };
    },

    // ---------- Envío ----------
    async onSubmit() {
      if (!this.isValid || this.isBusy) return;
      this.errorMessage = '';
      this.successMessage = '';

      let preview = null;
      let artifacts = null;

      if (this.useStudio) {
        this.status = 'building';
        try {
          artifacts = await this.buildStudioArtifacts();
          preview = await this.$refs.studio.capturePreview();
        } catch (err) {
          this.status = 'error';
          this.progressLabel = '';
          this.errorMessage = `No pudimos preparar los archivos de tu rompecabezas: ${err.message}`;
          return;
        }
      } else {
        preview = await this.canvasToBlob();
      }

      this.status = 'sending';
      this.progressLabel = '';

      const legacyCrop = {
        zoom: Number(this.zoom.toFixed(3)),
        offsetX: Math.round(this.offsetX),
        offsetY: Math.round(this.offsetY),
        canvasWidth: this.canvasWidth,
        canvasHeight: this.canvasHeight,
      };

      const { receipt, status, error } = await submitCustomOrder({
        productType: this.productType,
        sizeId: this.sizeId,
        pieces: this.productType === 'rompecabezas' ? this.pieces : null,
        caseOption: this.caseOption,
        crop: this.useStudio ? this.studio.crop : legacyCrop,
        // Parámetros del estudio: sin ellos el troquel no es reproducible.
        studio: this.useStudio ? this.studio : null,
        artifactMeta: artifacts
          ? { dieStl: artifacts.die.meta, imageSvg: artifacts.svg.meta }
          : null,
        die: artifacts?.die || null,
        svg: artifacts?.svg || null,
        personalizationText: this.personalizationText,
        contactName: this.contactName,
        contactEmail: this.contactEmail,
        contactPhone: this.contactPhone,
        notes: this.notes,
        image: this.file,
        preview,
      });

      if (error) {
        this.status = 'error';
        this.errorMessage = error;
        return;
      }

      if (status === 'processing') {
        this.status = 'processing';
        return;
      }

      this.status = 'done';
      this.successMessage = `Pedido recibido (#${receipt?.id ?? '—'}). Te confirmamos por correo a ${this.contactEmail}.`;
    },
  },
};
</script>

<style scoped>
.customizer {
  width: 100%;
  background: #F1F5F9;
  border-radius: 16px;
  padding: 2rem;
  box-sizing: border-box;
}

.head {
  margin-bottom: 1.5rem;
}

.head .subtitle {
  margin-bottom: 0.5rem;
}

.type-switch {
  display: flex;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
}

.type-btn {
  padding: 0.5rem 1.1rem;
  border-radius: 9999px;
  border: 1px solid #9DB2BF;
  background: white;
  color: #4F5665;
  font-family: inherit;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}

.type-btn.is-selected {
  background: #2E8A99;
  border-color: #2E8A99;
  color: white;
}

.type-btn:focus-visible {
  outline: 2px solid #526D82;
  outline-offset: 2px;
}

.layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;
}

.left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.preview-wrap {
  background: white;
  border: 1px solid #9DB2BF;
  border-radius: 8px;
  padding: 0.8rem;
}

.preview-canvas {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 6px;
  cursor: grab;
  touch-action: none;
}

.preview-canvas:active {
  cursor: grabbing;
}

.zoom {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-top: 0.8rem;
  font-size: 0.85rem;
  color: #4F5665;
}

.zoom input {
  flex: 1;
  accent-color: #526D82;
}

.hint {
  font-size: 0.8rem;
  color: #4F5665;
  margin: 0.4rem 0 0;
  font-style: italic;
}

.custom-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: white;
  border: 1px solid #9DB2BF;
  border-radius: 12px;
  padding: 1.2rem;
}

.form-title {
  font-family: "Inter", sans-serif;
  font-size: 1.05rem;
  color: #111827;
  margin: 0.4rem 0 0;
}

.block {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: #4F5665;
}

.custom-form input,
.custom-form select,
.custom-form textarea {
  border: 1px solid #0B132A;
  border-radius: 6px;
  padding: 0.55rem 0.7rem;
  font-size: 16px;
  font-family: inherit;
  color: #2C3E50;
  background: white;
  margin-bottom: 0 !important;
}

.custom-form textarea {
  resize: vertical;
}

.custom-form input:focus,
.custom-form select:focus,
.custom-form textarea:focus {
  outline: 2px solid #526D82;
  outline-offset: 1px;
}

.submit-btn {
  background: #526D82;
  color: white;
  border: none;
  padding: 0.7rem 1rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
}

.submit-btn:hover:not(:disabled) {
  background: #3D566B;
}

.submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.legal-note {
  font-size: 0.8rem;
  color: #4F5665;
  margin: 0;
  text-align: center;
}

.legal-note a {
  color: #526D82;
}

.state-msg {
  background: rgba(82, 109, 130, 0.1);
  color: #2C3E50;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  margin: 0;
  font-size: 0.9rem;
}

.ok-msg {
  background: rgba(22, 163, 74, 0.1);
  color: #166534;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  margin: 0;
  font-size: 0.9rem;
}

.err-msg {
  background: rgba(185, 28, 28, 0.08);
  color: #991B1B;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  margin: 0;
  font-size: 0.9rem;
}

/* Con el estudio, el panel de edición necesita más sitio que el formulario. */
.layout.is-studio {
  grid-template-columns: minmax(0, 1.55fr) minmax(280px, 1fr);
}

@media (max-width: 1100px) {
  .layout.is-studio {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 800px) {
  .customizer {
    padding: 1.2rem;
  }
  .layout {
    grid-template-columns: 1fr;
  }
}
</style>
